import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { Dropdown, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CreateGuideModal from './CreateGuideModal';
import DiscoverItemModal from './DiscoverItemModal';
import GuideListItem from './GuideListItem';
import {
  authHeaders,
  guidesFetch,
  GUIDE_LIST_TITLES
} from './guidesUtils';

const TYPE_ORDER = ['notebook', 'article', 'flashcards'];
const TYPE_LABELS = {
  notebook: 'Notebooks',
  article: 'Articles',
  flashcards: 'Flashcards'
};

function readStoredGuidesView() {
  try {
    const v = localStorage.getItem('guidesView');
    if (v === 'list' || v === 'grid') return v;
  } catch {
    // ignore
  }
  return 'list';
}

function navigateToItem(navigate, item, { preferEdit = false } = {}) {
  if (item.type === 'flashcard_deck') {
    navigate(`/guides/flashcards/decks/${item.id}`);
    return;
  }
  if (item.type === 'flashcard_set') {
    navigate(`/guides/flashcards/sets/${item.id}`);
    return;
  }
  if (preferEdit || item.isOwner || item.canEditCopy) {
    if (item.type === 'notebook') navigate(`/guides/notebooks/${item.id}`);
    else navigate(`/guides/articles/${item.id}/edit`);
    return;
  }
  if (item.type === 'notebook') navigate(`/guides/notebooks/${item.id}`);
  else navigate(`/guides/articles/${item.id}/read`);
}

function deletePath(item) {
  if (item.type === 'notebook') return `/notebooks/${item.id}`;
  if (item.type === 'article') return `/articles/${item.id}`;
  return null;
}

export default function GuidesListPage({ mode: modeProp }) {
  const location = useLocation();
  const navigate = useNavigate();
  const outlet = useOutletContext() || {};
  const mode =
    modeProp ||
    (location.pathname.includes('discover')
      ? 'discover'
      : location.pathname.includes('shared')
        ? 'shared'
        : 'mine');

  const [items, setItems] = useState([]);
  const [collectionItems, setCollectionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createType, setCreateType] = useState(null);
  const [discoverItem, setDiscoverItem] = useState(null);
  const [localView, setLocalView] = useState(readStoredGuidesView);
  const view = outlet.view ?? localView;
  const setViewMode = outlet.setViewMode || ((v) => {
    const next = v === 'list' ? 'list' : 'grid';
    setLocalView(next);
    try {
      localStorage.setItem('guidesView', next);
    } catch {
      // ignore
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await guidesFetch(`/${mode}`, { headers: authHeaders() });
      setItems(Array.isArray(data) ? data : []);
      if (mode === 'mine') {
        try {
          const collected = await guidesFetch('/collections?types=notebook,article', {
            headers: authHeaders()
          });
          setCollectionItems(Array.isArray(collected) ? collected : []);
        } catch {
          setCollectionItems([]);
        }
      } else {
        setCollectionItems([]);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load guides');
      setItems([]);
      setCollectionItems([]);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    if (mode === 'mine') return null;
    const groups = TYPE_ORDER.map((type) => {
      const groupItems = type === 'flashcards'
        ? items.filter((i) => i.type === 'flashcard_deck' || i.type === 'flashcard_set')
        : items.filter((i) => i.type === type);
      return {
        type,
        label: TYPE_LABELS[type],
        items: groupItems
      };
    }).filter((g) => g.items.length > 0);
    return groups;
  }, [items, mode]);

  const openItem = (item) => {
    if (mode === 'discover') {
      setDiscoverItem({ type: item.type, id: item.id });
      return;
    }
    if (item.isCollection) {
      navigateToItem(navigate, item, { preferEdit: !!item.canEditCopy });
      return;
    }
    navigateToItem(navigate, item, { preferEdit: mode === 'mine' && item.isOwner });
  };

  const removeItemFromState = (item) => {
    const match = (row) => !(row.type === item.type && row.id === item.id && (row.collectionId || '') === (item.collectionId || ''));
    setItems((prev) => prev.filter(match));
    setCollectionItems((prev) => prev.filter(match));
  };

  const handleDelete = async (item) => {
    const path = deletePath(item);
    if (!path) return;
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await guidesFetch(path, { method: 'DELETE', headers: authHeaders() });
      removeItemFromState(item);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const handleRemoveCollection = async (item) => {
    if (!item.collectionId) return;
    if (!window.confirm(`Remove "${item.title}" from your collection?`)) return;
    try {
      await guidesFetch(`/collections/${item.collectionId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      removeItemFromState(item);
      toast.success('Removed from collection');
    } catch (err) {
      toast.error(err.message || 'Failed to remove from collection');
    }
  };

  const handleCreated = (created) => {
    if (createType === 'article') navigate(`/guides/articles/${created.id}/edit`);
    else navigate(`/guides/notebooks/${created.id}`);
  };

  const title = GUIDE_LIST_TITLES[mode] || 'Guides';
  const totalCount = mode === 'mine' ? items.length + collectionItems.length : items.length;
  const countLabel =
    totalCount === 1
      ? '1 item'
      : `${totalCount} ${mode === 'mine' ? 'notebooks & articles' : 'items'}`;

  const renderCard = (item, keyPrefix = '') => (
    <GuideListItem
      key={`${keyPrefix}${item.type}-${item.id}-${item.collectionId || ''}`}
      item={item}
      view={view}
      onOpen={() => openItem(item)}
      onDelete={mode === 'mine' && item.isOwner && !item.readOnly ? () => handleDelete(item) : null}
      onRemoveCollection={item.isCollection ? () => handleRemoveCollection(item) : null}
    />
  );

  return (
    <div className="guides-list-page">
      <div className="guides-list-header">
        <div>
          <h6 className="mb-0 fw-semibold">{title}</h6>
          <p className="guides-page-meta">{countLabel}</p>
        </div>
        <div className="guides-list-actions">
          <div className="radio-inputs" style={{ width: 'fit-content' }}>
            <label className="radio">
              <input
                type="radio"
                name="guides-view-radio"
                checked={view === 'list'}
                onChange={() => setViewMode('list')}
                aria-label="List view"
              />
              <span className="name px-md-3 p-2">
                <i className="bi bi-list me-1" />
                <span className="d-none d-md-inline">List</span>
              </span>
            </label>
            <label className="radio">
              <input
                type="radio"
                name="guides-view-radio"
                checked={view === 'grid'}
                onChange={() => setViewMode('grid')}
                aria-label="Thumbnail view"
              />
              <span className="name px-md-3 p-2">
                <i className="bi bi-grid-3x3-gap me-1" />
                <span className="d-none d-md-inline">Grid</span>
              </span>
            </label>
          </div>
          {mode === 'mine' && (
            <Dropdown align="end">
              <Dropdown.Toggle variant="outline-primary" size="sm" className="d-flex align-items-center gap-1">
                <i className="bi bi-plus-lg" />
                <span className="d-none d-md-inline">New</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setCreateType('notebook')}>Notebook</Dropdown.Item>
                <Dropdown.Item onClick={() => setCreateType('article')}>Article</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
      </div>

      {loading ? (
        <div className="guides-loading"><Spinner /></div>
      ) : totalCount === 0 ? (
        <div className="guides-empty card border shadow-none p-4">
          <p className="mb-0 text-muted">
            {mode === 'mine' && 'Create a notebook or article to get started.'}
            {mode === 'discover' && 'No published notebooks, articles, or flashcards in your program yet.'}
            {mode === 'shared' && 'Nothing has been shared with you yet.'}
          </p>
        </div>
      ) : mode === 'mine' ? (
        <div className="guides-grouped-list">
          {items.length > 0 && (
            <section className="guides-group-section mb-4">
              <h6 className="guides-group-heading smaller text-uppercase text-muted fw-semibold mb-2">
                My content
              </h6>
              <div className={view === 'grid' ? 'guides-grid' : 'guides-list-rows'}>
                {items.map((item) => renderCard(item, 'mine-'))}
              </div>
            </section>
          )}
          {collectionItems.length > 0 && (
            <section className="guides-group-section mb-4">
              <h6 className="guides-group-heading smaller text-uppercase text-muted fw-semibold mb-2">
                Collection
              </h6>
              <div className={view === 'grid' ? 'guides-grid' : 'guides-list-rows'}>
                {collectionItems.map((item) => renderCard(item, 'col-'))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="guides-grouped-list">
          {grouped.map((group) => (
            <section key={group.type} className="guides-group-section mb-4">
              <h6 className="guides-group-heading smaller text-uppercase text-muted fw-semibold mb-2">
                {group.label}
              </h6>
              <div className={view === 'grid' ? 'guides-grid' : 'guides-list-rows'}>
                {group.items.map((item) => renderCard(item))}
              </div>
            </section>
          ))}
        </div>
      )}

      <CreateGuideModal
        show={!!createType}
        type={createType || 'notebook'}
        onHide={() => setCreateType(null)}
        onCreated={handleCreated}
      />

      <DiscoverItemModal
        show={!!discoverItem}
        onHide={() => setDiscoverItem(null)}
        targetType={discoverItem?.type}
        targetId={discoverItem?.id}
        onOpen={(detail) => navigateToItem(navigate, detail)}
      />
    </div>
  );
}
